import { type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './UnitBanner.css';

export type UnitBannerTone = 'primary' | 'accent' | 'xp' | 'info';

export interface UnitBannerProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Unit number label (e.g. "ნაწილი 1"). Small pill above the title. */
  kicker?: ReactNode;
  /** Unit title (Georgian). */
  title: ReactNode;
  /** One-line unit description. */
  description?: ReactNode;
  /** Color tone — each unit in the course should pick a different one to
   *  make vertical scanning through the tree feel distinct. */
  tone?: UnitBannerTone;
  /** Optional trailing icon / emoji / decorative glyph. */
  icon?: ReactNode;
  ref?: Ref<HTMLElement>;
}

/**
 * UnitBanner — the full-width colored divider between units on the home
 * skill tree. Per DECISIONS 2026-04-21 each A1 unit owns a "cover art
 * slot" — until we commission illustrations, the `icon` slot holds a
 * stand-in emoji. The banner itself carries a bold colored background so
 * the tree reads as "chapters" visually.
 */
export function UnitBanner({
  kicker,
  title,
  description,
  tone = 'primary',
  icon,
  className,
  ref,
  ...rest
}: UnitBannerProps) {
  return (
    <section
      ref={ref}
      className={clsx('fluent-unitbanner', className)}
      data-tone={tone}
      {...rest}
    >
      <div className="fluent-unitbanner__inner">
        <div className="fluent-unitbanner__text">
          {kicker != null && <p className="fluent-unitbanner__kicker">{kicker}</p>}
          <h2 className="fluent-unitbanner__title">{title}</h2>
          {description != null && (
            <p className="fluent-unitbanner__description">{description}</p>
          )}
        </div>
        {icon != null && <div className="fluent-unitbanner__icon" aria-hidden="true">{icon}</div>}
      </div>
    </section>
  );
}
